import React, { useState, useEffect } from 'react';
import { Container, Tabs, Tab, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';

import Table from '../../Components/Table/Table';

import { BankConfigDataService } from '../../Services/BankConfigDataService';

import CompanyModal from './CompanyModal';
import BankAuthModal from './BankAuthModal';
import BankServiceModal from './BankServiceModal';
import AccountModal from './AccountModal';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';

const BankConfigPageContent = () => {
  const { instance, accounts, inProgress } = useMsal();

  /* ------------- shared helpers -------------------- */
  const token = async () => {
    const req = { ...loginRequest, account: accounts[0] };
    return (await instance.acquireTokenSilent(req)).accessToken;
  };

  /* ------------- state buckets per tab ------------- */
  const [tab, setTab] = useState('companies');

  const [rows,  setRows]  = useState([]);
  const [edit,  setEdit]  = useState(null);
  const [delId, setDelId] = useState(null);

  /* ------------- loader ----------------------------- */
  const load = async () => {
    if (inProgress !== 'none' || !accounts.length) return;
    const t = await token();
    const svc = BankConfigDataService;
    switch (tab) {
      case 'companies':    setRows(await svc.companies.getAll(t)); break;
      case 'auths':        setRows(await svc.bankAuths.getAll(t)); break;
      case 'services':     setRows(await svc.bankServices.getAll(t)); break;
      case 'accounts':     setRows(await svc.accounts.getAll(t)); break;
    }
  };
  useEffect(() => { load(); }, [tab, inProgress]);

  /* ------------- save & delete ---------------------- */
  const handleSave = async (dto) => {
    const t = await token();
    const svc = BankConfigDataService;
    if (tab==='companies')  edit ? await svc.companies.update(edit.companyId,dto,t)
                                 : await svc.companies.create(dto,t);
    if (tab==='auths')      edit ? await svc.bankAuths.update(edit.bankAuthId,dto,t)
                                 : await svc.bankAuths.create(dto,t);
    if (tab==='services')   edit ? await svc.bankServices.update(edit.bankServiceId,dto,t)
                                 : await svc.bankServices.create(dto,t);
    if (tab==='accounts')   edit ? await svc.accounts.update(edit.companyBankAccountId,dto,t)
                                 : await svc.accounts.create(dto,t);
    setEdit(null); load();
  };

  const handleDelete = async () => {
    const t = await token();
    const svc = BankConfigDataService;
    if (tab==='companies')   await svc.companies.remove(delId,t);
    if (tab==='auths')       await svc.bankAuths.remove(delId,t);
    if (tab==='services')    await svc.bankServices.remove(delId,t);
    if (tab==='accounts')    await svc.accounts.remove(delId,t);
    setDelId(null); load();
  };

  /* ------------- column sets ------------------------ */
  const cols = {
    companies: [
      { header:'ID', accessorKey:'companyId' },
      { header:'Name',accessorKey:'companyName' },
      { header:'Tax ID',accessorKey:'taxId' },
      { header:'Actions', id:'act', cell:ActionCell }
    ],
    auths: [
      { header:'ID', accessorKey:'bankAuthId'},
      { header:'Bank',accessorKey:'bankName'},
      { header:'Country',accessorKey:'countryCode'},
      { header:'BIC',accessorKey:'bankBic'},
      { header:'ApiKey',accessorKey:'apiKey'},
      { header:'Actions', id:'act', cell:ActionCell }
    ],
    services: [
      { header:'ID', accessorKey:'bankServiceId'},
      { header:'Bank Config',accessorKey:'bankDesc'},
      { header:'Name',accessorKey:'serviceName'},
      { header:'BaseUrl',accessorKey:'baseUrl'},
      { header:'Endpoint',accessorKey:'endpointPath'},
      { header:'Actions', id:'act', cell:ActionCell }
    ],
    accounts: [
      { header:'ID', accessorKey:'companyBankAccountId'},
      { header:'Company',accessorKey:'companyName'},
      { header:'Service',accessorKey:'serviceName'},
      { header:'Account',accessorKey:'accountNumber'},
      { header:'Currency',accessorKey:'accountCurrency'},
      { header:'Actions', id:'act', cell:ActionCell }
    ]
  };

  function ActionCell({ row }) {
    return (
      <div className='d-flex gap-2'>
        <Button size='sm' variant='outline-secondary'
                onClick={() => setEdit(row.original)}>
          <FontAwesomeIcon icon={faPen}/>
        </Button>
        <Button size='sm' variant='outline-danger'
                onClick={() => setDelId(row.original[ Object.keys(row.original)[0] ])}>
          <FontAwesomeIcon icon={faTrash}/>
        </Button>
      </div>
    );
  }

  /* ------------- modal selector --------------------- */
  const renderModal = () => {
    const props = { show:!!edit, onHide:()=>setEdit(null), onSave:handleSave, initial:edit };
    if (!edit) return null;
    if (tab==='companies') return <CompanyModal {...props}/>;
    if (tab==='auths')     return <BankAuthModal {...props}/>;
    if (tab==='services')  return <BankServiceModal {...props}/>;
    return <AccountModal {...props}/>;
  };

  return (
    <Container fluid className='h-100 d-flex flex-column'>
      <div className='d-flex justify-content-end mb-2'>
        <Button variant='outline-primary' className='me-2' onClick={load}>
          <FontAwesomeIcon icon={faRefresh}/>
        </Button>
        <Button variant='primary' onClick={()=>setEdit({})}>
          <FontAwesomeIcon icon={faPlus}/> New
        </Button>
      </div>

      <Tabs activeKey={tab} onSelect={k=>setTab(k)} className='mb-3'>
        <Tab eventKey='companies' title='Companies'/>
        <Tab eventKey='auths'     title='Bank Auths'/>
        <Tab eventKey='services'  title='Services'/>
        <Tab eventKey='accounts'  title='Accounts'/>
      </Tabs>

      <Table
        columns={cols[tab]}
        data={rows}
        tableClass='table-hover'
        headerClass='table-light'
        containerClass='table-responsive'
      />

      {renderModal()}

      {delId !== null && (
        <ConfirmationModal
          show={true}
          title='Delete record'
          content='Are you sure?'
          onHide={()=>setDelId(null)}
          onCancelClick={()=>setDelId(null)}
          onAcceptClick={handleDelete}
        /> )}
    </Container>
  );
};

const BankConfigPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title='Bank Statement Configurations'>
      <BankConfigPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default BankConfigPage;
